import { useState, useCallback } from 'react';
import {
  useSensor,
  useSensors,
  PointerSensor,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { notifications } from '@mantine/notifications';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { groupsApi } from '../../../../services/api/groups';
import type { DraggableParticipantData } from '../DraggableParticipantCard';

export interface CapacityWarning {
  participant: DraggableParticipantData;
  targetGroup: GroupWithMembers;
}

export interface PendingAssignment {
  participant: DraggableParticipantData;
  targetGroup: GroupWithMembers;
}

interface UseGroupDragDropOptions {
  cohortId: string;
  groups: GroupWithMembers[];
  onRefresh: () => void | Promise<void>;
  /** When set, intercepts assignment for confirmation before executing */
  onBeforeAssign?: (pending: PendingAssignment) => boolean;
}

export interface UseGroupDragDropResult {
  /** The participant currently being dragged (for DragOverlay) */
  activeDragParticipant: DraggableParticipantData | null;
  /** Sensor configuration for dnd-kit */
  sensors: ReturnType<typeof useSensors>;
  /** Handler for drag start events */
  handleDragStart: (event: DragStartEvent) => void;
  /** Handler for drag end events — executes assignment or removal */
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  /** Pending capacity warning (group at preferred limit) */
  capacityWarning: CapacityWarning | null;
  /** Confirm the capacity override and assign the participant */
  confirmCapacityOverride: () => Promise<void>;
  /** Dismiss the capacity warning without assigning */
  dismissCapacityWarning: () => void;
}

/**
 * Manages drag-and-drop state and handlers for moving participants
 * between groups and the unassigned section.
 */
export function useGroupDragDrop({
  cohortId,
  groups,
  onRefresh,
  onBeforeAssign,
}: UseGroupDragDropOptions): UseGroupDragDropResult {
  const [activeDragParticipant, setActiveDragParticipant] =
    useState<DraggableParticipantData | null>(null);
  const [capacityWarning, setCapacityWarning] = useState<CapacityWarning | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const participant = active.data.current?.participant as DraggableParticipantData | undefined;
    if (participant) {
      setActiveDragParticipant(participant);
    }
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragParticipant(null);

      if (!over) return;

      const participant = active.data.current?.participant as DraggableParticipantData | undefined;
      if (!participant) return;

      const dropId = String(over.id);

      // ── Drop on unassigned area → remove from group ──
      if (dropId === 'unassigned') {
        if (participant.group_id) {
          const currentGroup = groups.find((g) => g.id === participant.group_id);
          if (currentGroup) {
            try {
              await groupsApi.removeMember(cohortId, currentGroup.id, participant.user_id);
              notifications.show({
                title: 'Participant Removed',
                message: `${participant.first_name} ${participant.last_name} has been removed from ${currentGroup.name}`,
                color: 'green',
              });
              await onRefresh();
            } catch (error) {
              notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to remove participant',
                color: 'red',
              });
            }
          }
        }
        return;
      }

      // ── Drop on a group → assign to that group ──
      if (dropId.startsWith('group-')) {
        const groupId = dropId.replace('group-', '');
        const targetGroup = groups.find((g) => g.id === groupId);

        if (!targetGroup) return;
        if (participant.group_id === groupId) return;

        const PREFERRED_CAPACITY = 8;
        if (targetGroup.member_count >= PREFERRED_CAPACITY) {
          setCapacityWarning({ participant, targetGroup });
          return;
        }

        // Intercept for confirmation modal (MED-620)
        if (onBeforeAssign?.({ participant, targetGroup })) {
          return;
        }

        try {
          await groupsApi.assignMember(cohortId, groupId, participant.user_id);
          notifications.show({
            title: 'Participant Assigned',
            message: `${participant.first_name} ${participant.last_name} has been assigned to ${targetGroup.name}`,
            color: 'green',
          });
          await onRefresh();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to assign participant',
            color: 'red',
          });
        }
      }
    },
    [cohortId, groups, onRefresh, onBeforeAssign],
  );

  const confirmCapacityOverride = useCallback(async () => {
    if (!capacityWarning) return;
    const { participant, targetGroup } = capacityWarning;
    setCapacityWarning(null);

    // Intercept for confirmation modal (MED-620)
    if (onBeforeAssign?.({ participant, targetGroup })) {
      return;
    }

    try {
      await groupsApi.assignMember(cohortId, targetGroup.id, participant.user_id);
      notifications.show({
        title: 'Participant Assigned',
        message: `${participant.first_name} ${participant.last_name} has been assigned to ${targetGroup.name}`,
        color: 'green',
      });
      await onRefresh();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to assign participant',
        color: 'red',
      });
    }
  }, [capacityWarning, cohortId, onRefresh, onBeforeAssign]);

  const dismissCapacityWarning = useCallback(() => {
    setCapacityWarning(null);
  }, []);

  return {
    activeDragParticipant,
    sensors,
    handleDragStart,
    handleDragEnd,
    capacityWarning,
    confirmCapacityOverride,
    dismissCapacityWarning,
  };
}
